/**
 * Is this string the GitHub sign-in URL `copilot login` printed — the one the Copilot sign-in job
 * streams as its `login-url` progress event, for the member to open?
 *
 * Rendering CLI output as an `<a href>` without a check is how a link becomes an attack surface, so
 * this is an allowlist: https, exactly `github.com`, no credentials or port, and either the device
 * page (`/login/device`, where the member types the code) or the OAuth authorize page whose
 * `redirect_uri` is the CLI's own `http://127.0.0.1:<port>/callback` listener — opening that one in
 * any browser on this machine completes the sign-in, no code needed.
 *
 * The passerelle validates the same URL on its side (`client/passerelle/copilot-auth.ts`) — a
 * deliberate duplicate: the web and the passerelle are separate subprojects and cannot import each
 * other (CLAUDE.md rule A).
 *
 * Its own module rather than a helper in `lib/api.ts`, for the same reason as claude-login-url.ts:
 * the component tests mock `../lib/api` wholesale.
 */

const CALLBACK_HOSTS = new Set(['127.0.0.1', 'localhost'])

const MAX_URL_CHARS = 4096

export function isCopilotLoginUrl(s: unknown): s is string {
  if (typeof s !== 'string' || s.length === 0 || s.length > MAX_URL_CHARS) return false

  let url: URL
  try {
    url = new URL(s)
  } catch {
    return false
  }

  if (url.protocol !== 'https:' || url.hostname !== 'github.com') return false
  if (url.username !== '' || url.password !== '' || url.port !== '') return false
  if (url.pathname === '/login/device') return true
  if (url.pathname !== '/login/oauth/authorize') return false

  const redirect = url.searchParams.get('redirect_uri')
  if (!redirect) return false
  let redirectUrl: URL
  try {
    redirectUrl = new URL(redirect)
  } catch {
    return false
  }
  return redirectUrl.protocol === 'http:' && CALLBACK_HOSTS.has(redirectUrl.hostname) && redirectUrl.pathname === '/callback'
}

/** GitHub's device-flow user code (`login-code` progress event): two groups of four, `ABCD-1234`. */
export function isCopilotDeviceCode(s: unknown): s is string {
  return typeof s === 'string' && /^[A-Z0-9]{4}-[A-Z0-9]{4}$/.test(s)
}
